import { AxiosError } from 'axios'
import { PluginConfigType } from 'Core/PluginCore/PluginsManager'
import type { CSATFeedbackData } from 'Types/CSAT.types'
import {
    cancelTaskApi,
    createSubmissionApi,
    fillDraftDataApi,
    getAssignmentAtStepApi,
    getAssignmentScopesApi,
    getFeedbackPreviewApi,
    getFileDownloadUrlApi,
    getJobHierarchyApi,
    getJobOutputsApi,
    getJobPreviewApi,
    getOnHoldAssignmentApi,
    getOnHoldTaskDetailsApi,
    getPluginsAssociationsAtStepApi,
    getPreviewTaskApi,
    getPreviewTaskForExpertApi,
    getSignedURLSApi,
    getSignedURLSByPathAPI,
    getStepLevelTaskDetailsApi,
    getTaskDetailsApi,
    getTasksOutputByJobApi,
    getUserJobHistoryApi,
    getUserProjectHistoryApi,
    getUserTaskFeedbackApi,
    getWorkflowStepsByJobIdApi,
    holdTaskByUserApi,
    resumeTaskByUserApi,
    saveTaskApi,
    submitTaskApi,
    submitTurnDataApi,
    testToolCallApi,
    submitTurnDataStreamingApi,
    uploadFileAPI,
} from 'HTTPClients/tasks.client'

export interface TaskApiErrorResponse {
    response: {
        data: {
            success: false
            error: {
                code: string
                message: string
                meta_data: null | Record<string, unknown>
            }
        }
    }
}
import { FileValueType } from 'Types/Attribute.types'
import {
    AssignmentAtStepType,
    AssignmentScopeResponseType,
    ContainerDataType,
    ContainerType,
    CreateSubmissionType,
    GetOnHoldTaskDetailsResponseType,
    JobTasksOutput,
    StatusType,
    SubmissionPlayTurnAPIResponseType,
    TaskStatusLabelType,
    UserJobHistory,
    UserProjectHistoryResponseDTO,
    TaskStatusType,
    SubmissionPlayTurnV2APIResponseType,
    ConversationMainInfoType,
    Conversationsv2MainInfoType,
    ConversationalTurnTypes,
    Conversationalv2TurnTypes,
    ToolCallResponseType,
    SubmitTurnDataPayload,
} from 'Types/Tasks.types'
import { sanitizeAxiosError } from 'Utils/common.utils'
import dayjs from 'dayjs'
import { appConfig } from 'Config/app-config'
import { ApiError, JSONObject } from 'Types/Common.types'

export type AssignmentResponseType = {
    assignmentResp: AssignmentAtStepType<ContainerType, unknown>
    submissionResp?: CreateSubmissionType
}

export const getAssignmentScopes = async (
    userId: string,
    limit: number,
    offset: number
): Promise<AssignmentScopeResponseType[] | undefined> => {
    const assignmentScope = await getAssignmentScopesApi(userId, limit, offset)
    if (!assignmentScope.isOk()) {
        throw new Error(
            sanitizeAxiosError(assignmentScope.error) ?? 'Failed to fetch assignment scopes'
        )
    }
    const assignmentScopeResp = assignmentScope.value.scopes
    return assignmentScopeResp.map(scope => ({
        projectName: scope.project.name,
        projectNickname: scope.project.nickname,
        workflowUseCase: scope.workflow.name,
        stepType: scope.step.type,
        projectDescription: scope.project.description,
        stepId: scope.step.id,
        stepName: scope.step.name,
        projectId: scope.project.id,
        useCase: scope.workflow.use_case,
    }))
}

export const getOnHoldTaskDetails = async (payload: {
    user_id: string
    step_id: string
}): Promise<GetOnHoldTaskDetailsResponseType | undefined> => {
    const holdTaskResp = await getOnHoldTaskDetailsApi(payload)
    if (!holdTaskResp.isOk()) {
        throw new Error(
            sanitizeAxiosError(holdTaskResp.error) ?? 'Failed to fetch on-hold task details'
        )
    }

    return {
        project: {
            id: holdTaskResp.value.project.id,
            name: holdTaskResp.value.project.name,
            nickname: holdTaskResp.value.project.nick_name,
        },
        workflow: {
            id: holdTaskResp.value.workflow.id,
            name: holdTaskResp.value.workflow.name,
            useCase: holdTaskResp.value.workflow.use_case,
            pipeline: holdTaskResp.value.workflow.pipeline,
        },
        step: {
            id: holdTaskResp.value.step.id,
            name: holdTaskResp.value.step.name,
            type: holdTaskResp.value.step.type,
        },
        inProgressTaskId: holdTaskResp.value.in_progress_task_id,
        onHoldTasks: holdTaskResp.value.on_hold_tasks?.map(task => ({
            taskId: task.task_id,
            jobId: task.job_id,
            submissionId: task.submission_id,
            savedReason: task.saved_reason,
            lastSavedAt: dayjs(task.last_saved_at).format('HH:MM, DD MMM YYYY'),
            taskStatus: task.task_status,
        })),
    }
}

export const resumeTaskByUser = async (payload: {
    id: string
    user_id: string
}): Promise<boolean> => {
    const resumeTaskResp = await resumeTaskByUserApi(payload)
    if (!resumeTaskResp.isOk()) {
        throw new Error(sanitizeAxiosError(resumeTaskResp.error) ?? 'Failed to resume task')
    }
    return true
}

export const holdTaskByUser = async (payload: {
    userId: string
    submissionId: string
    timeElapsedInSeconds: number
    draftOutputContainer: ContainerDataType<ContainerType, unknown>
    message?: string
}): Promise<boolean> => {
    const holdTaskResp = await holdTaskByUserApi(payload)
    if (!holdTaskResp.isOk()) {
        throw new Error(sanitizeAxiosError(holdTaskResp.error) ?? 'Failed to hold task')
    }
    return true
}

export const getAssignmentAtStep = async (
    userId: string,
    stepId: string,
    projectId: string
): Promise<AssignmentResponseType | undefined> => {
    const assignmentResp = await getAssignmentAtStepApi(userId, stepId, projectId)
    if (!assignmentResp.isOk()) {
        const error = assignmentResp.error as AxiosError<{
            error?: ApiError
        }>
        const errorCode = error.response?.data?.error?.code
        const errorMessageFromResponse = error.response?.data?.error?.message
        const errorMessage =
            errorMessageFromResponse ?? sanitizeAxiosError(error) ?? 'Failed to fetch assignment'
        const errorObject = new Error(errorMessage)
        ;(errorObject as ApiError).code = errorCode
        throw errorObject
    }

    if (
        assignmentResp.value.assignment.status === StatusType.ASSIGNED ||
        assignmentResp.value.assignment.status === StatusType.TO_BE_ASSIGNED
    ) {
        const submissionResp = await createSubmissionApi(assignmentResp.value.assignment.id)
        if (!submissionResp.isOk()) {
            console.error(submissionResp.error)
            throw new Error(
                sanitizeAxiosError(submissionResp.error) ?? 'Failed to create submission'
            )
        }
        return {
            assignmentResp: assignmentResp.value,
            submissionResp: submissionResp.value,
        }
    }
    return {
        assignmentResp: assignmentResp.value,
    }
}

export const getUserTaskFeedback = async (userId: string) => {
    const resp = await getUserTaskFeedbackApi(userId)
    if (!resp.isOk()) {
        throw new Error(sanitizeAxiosError(resp.error) ?? 'Failed to get user task feedback')
    }
    return resp.value
}

export const getUserJobHistory = async (payload: {
    limit: number
    offset: number
    jobId?: string
    taskId?: string
    workflowId?: string
    projectId?: string
}): Promise<UserJobHistory> => {
    const resp = await getUserJobHistoryApi(payload)
    if (!resp.isOk()) {
        throw new Error(sanitizeAxiosError(resp.error) ?? 'Failed to get user job history')
    }
    return resp.value
}

export const getUserProjectHistory = async (): Promise<UserProjectHistoryResponseDTO> => {
    const resp = await getUserProjectHistoryApi()
    if (!resp.isOk()) {
        throw new Error(sanitizeAxiosError(resp.error) ?? 'Failed to get user project history')
    }
    return resp.value
}

export const getTasksOutputByJob = async (
    jobId: string
): Promise<JobTasksOutput<ContainerType>> => {
    const resp = await getTasksOutputByJobApi(jobId)
    if (!resp.isOk()) {
        throw new Error(sanitizeAxiosError(resp.error) ?? 'Failed to get task feedback')
    }
    return {
        taskOutput:
            resp.value.task_output?.map(task => ({
                taskId: task.task_id,
                stepType: task.step_type,
                isCurrentUserStep: task.is_current_user_step,
                stepName: task.step_name,
                result: task.result,
                output: task.output,
            })) ?? [],
    }
}

export const getJobOutputs = async (
    jobId: string,
    taskId?: string
): Promise<JobTasksOutput<ContainerType>> => {
    const resp = await getJobOutputsApi(jobId, taskId)
    if (!resp.isOk()) {
        throw new Error(sanitizeAxiosError(resp.error) ?? 'Failed to get job outputs')
    }
    return {
        taskOutput:
            resp.value.task_output?.map(task => ({
                taskId: task.task_id,
                stepType: task.step_type,
                isCurrentUserStep: task.is_current_user_step,
                stepName: task.step_name,
                result: task.result,
                output: task.output,
            })) ?? [],
    }
}

export const getJobPreview = async (
    jobId: string,
    stepId: string
): Promise<AssignmentResponseType | undefined> => {
    const details = await getJobPreviewApi(jobId, stepId)
    if (!details.isOk()) {
        return
    }
    return {
        assignmentResp: details.value,
    }
}

const attachOutputHistoryToAssignment = async (
    assignmentData: AssignmentAtStepType<ContainerType, unknown>,
    taskId?: string
) => {
    const jobId = assignmentData.assignment.task.job_id
    let outputsResp: Awaited<ReturnType<typeof getJobOutputsApi>> | undefined =
        await getJobOutputsApi(jobId, taskId)
    if (!outputsResp.isOk()) {
        outputsResp = undefined
    }

    if (outputsResp) {
        assignmentData.assignment.output_history = (outputsResp.value.task_output ?? []).map(
            task => ({
                task_id: task.task_id,
                is_current_user_step: task.is_current_user_step,
                result: task.result,
                step_type: task.step_type,
                step_name: task.step_name,
                output: task.output,
            })
        )
    }

    return assignmentData
}

const ensureOutputHistoryForFeedbackPreview = (
    assignmentData: AssignmentAtStepType<ContainerType, unknown>
) => {
    const { task, step } = assignmentData.assignment
    const outputContainer = task.output_start_state_container

    if (assignmentData.assignment.output_history?.length || !outputContainer || !step) {
        return assignmentData
    }

    assignmentData.assignment.output_history = [
        {
            task_id: task.id,
            is_current_user_step: true,
            step_type: step.type,
            step_name: step.name,
            output: outputContainer,
        },
    ]

    return assignmentData
}

export const getTaskFeedbackPreview = async (
    taskId: string,
    userId: string
): Promise<AssignmentResponseType | undefined> => {
    const details = await getFeedbackPreviewApi(taskId, userId)
    if (!details.isOk()) {
        return
    }

    return {
        assignmentResp: ensureOutputHistoryForFeedbackPreview(details.value),
    }
}

export const getTaskDetails = async (
    taskId: string
): Promise<AssignmentResponseType | undefined> => {
    const details = await getTaskDetailsApi(taskId)
    if (!details.isOk()) {
        throw new Error(sanitizeAxiosError(details.error) ?? 'Failed to get task details')
    }
    return {
        assignmentResp: details.value,
    }
}

export const saveTaskSubmission = async (taskDetails: {
    userId: string
    submissionId: string
    timeElapsedInSeconds: number
    draftOutputContainer: ContainerDataType<ContainerType, unknown>
}): Promise<boolean> => {
    const saveTaskSubmission = await saveTaskApi(taskDetails)
    if (!saveTaskSubmission.isOk()) {
        throw saveTaskSubmission.error
    }
    return true
}

export const submitTaskSubmission = async (taskDetails: {
    userId: string
    submissionId: string
    timeElapsedInSeconds: number
    draftOutputContainer: ContainerDataType<ContainerType, unknown>
    pluginData: Record<string, unknown>
    feedbackData?: CSATFeedbackData
}): Promise<boolean> => {
    const submitTaskSubmission = await submitTaskApi(taskDetails)
    if (!submitTaskSubmission.isOk()) {
        throw submitTaskSubmission.error as TaskApiErrorResponse
    }
    return true
}

export const cancelTaskSubmission = async (
    id: string,
    message: string,
    skipReasons?: string[],
    feedbackData?: CSATFeedbackData
): Promise<boolean> => {
    const cancelTaskSubmission = await cancelTaskApi(id, message, skipReasons, feedbackData)
    if (!cancelTaskSubmission.isOk()) {
        throw cancelTaskSubmission.error as TaskApiErrorResponse
    }
    return false
}

export const getEnabledPluginsAtStep = async (stepId: string): Promise<PluginConfigType[]> => {
    const resp = await getPluginsAssociationsAtStepApi(stepId)
    if (!resp.isOk()) {
        console.error(resp.error)
        return []
    }
    const pluginConfigResponses = resp.value.task_level_plugin_configs

    return pluginConfigResponses
        .filter(p => p.enabled)
        .map(p => {
            return {
                id: p.id,
                type: p.type,
                config: p.config,
                usageStats: p.usage_stats,
            }
        })
}

export const getSignedOrPublicURL = async (url: string): Promise<string> => {
    const resp = await getSignedURLSApi(url)
    if (!resp.isOk()) {
        console.error(resp.error.message)
        return ''
    }
    return resp.value
}

export const uploadFile = async (
    file: File,
    path: string,
    taskId: string
): Promise<FileValueType> => {
    const signedURLResp = await getSignedURLSByPathAPI(taskId, path)
    if (!signedURLResp.isOk()) {
        throw new Error(sanitizeAxiosError(signedURLResp.error) ?? 'Failed to upload file')
    }

    const uploadUrl = Object.values(signedURLResp.value.data)[0].upload_url

    const uploadFileResp = await uploadFileAPI(file, uploadUrl)
    if (!uploadFileResp.isOk()) {
        throw new Error(sanitizeAxiosError(uploadFileResp.error) ?? 'Failed to upload file')
    }

    return {
        download_url: new URL(
            'file-download/?fileName=' + encodeURIComponent(path),
            appConfig.ftsConsoleUrl
        ).toString(),
        path: path,
    }
}

export const getFileDownloadUrl = async (filePath: string): Promise<string> => {
    const resp = await getFileDownloadUrlApi(filePath)
    if (!resp.isOk()) {
        throw new Error(sanitizeAxiosError(resp.error) ?? 'Failed to get file download URL')
    }
    return Object.values(resp.value.download_urls)[0]
}

export const getPreviewTaskForExpert = async (
    taskId: string,
    userId: string
): Promise<AssignmentResponseType | undefined> => {
    const resp = await getPreviewTaskForExpertApi(taskId, userId)
    if (!resp.isOk()) {
        throw new Error(sanitizeAxiosError(resp.error) ?? 'Failed to load the preview')
    }
    return {
        assignmentResp: resp.value,
    }
}

export const getPreviewTask = async (
    taskId: string
): Promise<AssignmentResponseType | undefined> => {
    const resp = await getPreviewTaskApi(taskId)
    if (!resp.isOk()) {
        throw new Error(sanitizeAxiosError(resp.error) ?? 'Failed to load the preview')
    }

    const assignmentData = await attachOutputHistoryToAssignment(resp.value, taskId)

    return {
        assignmentResp: assignmentData,
    }
}

export const getStepLevelTaskDetails = async (
    userId: string,
    stepId: string
): Promise<TaskStatusType[]> => {
    const relevantStatus = ['TO_BE_ASSIGNED', 'IN_PROGRESS', 'ASSIGNED'] as const

    const resp = await getStepLevelTaskDetailsApi(userId, stepId)

    if (!resp.isOk()) {
        throw new Error(sanitizeAxiosError(resp.error) ?? 'Failed to load step level task details')
    }

    const taskDetails = resp.value
    return [
        {
            label: TaskStatusLabelType.TasksAvailable,
            value: taskDetails.task_counts.reduce((acc, curr) => {
                if (relevantStatus.includes(curr.status as (typeof relevantStatus)[number])) {
                    acc += curr.count
                }
                return acc
            }, 0),
        },
        {
            label: TaskStatusLabelType.TasksCompleted,
            value: taskDetails.task_counts.find(task => task.status === 'COMPLETE')?.count ?? 0,
        },
        {
            label: TaskStatusLabelType.TasksSavedForLater,
            value: taskDetails.task_counts.find(task => task.status === 'ON_HOLD')?.count ?? 0,
        },
        {
            label: TaskStatusLabelType.AverageTimeTakenForTask,
            value: taskDetails.average_time_in_sec
                ? `${(taskDetails.average_time_in_sec / 60).toFixed(1)} min`
                : 'NA',
        },
    ]
}

export const getOnHoldAssignmentScopes = async (
    userId: string
): Promise<AssignmentScopeResponseType[] | undefined> => {
    const onholdAssignmentScope = await getOnHoldAssignmentApi(userId)
    if (!onholdAssignmentScope.isOk()) {
        return
    }
    const assignmentScopeResp = onholdAssignmentScope.value.scopes

    return assignmentScopeResp.map(scope => ({
        projectName: scope.project.name,
        projectNickname: scope.project.nickname,
        workflowUseCase: scope.workflow.name,
        stepType: scope.step.type,
        projectDescription: scope.project.description,
        stepId: scope.step.id,
        stepName: scope.step.name,
        projectId: scope.project.id,
        useCase: scope.workflow.use_case,
        taskCount: scope.task_count || 0,
    }))
}

export const submitTurnData = async (
    payload: SubmitTurnDataPayload
): Promise<SubmissionPlayTurnAPIResponseType | SubmissionPlayTurnV2APIResponseType | undefined> => {
    const resp = await submitTurnDataApi(payload)
    if (!resp.isOk()) {
        throw new Error(sanitizeAxiosError(resp.error) ?? 'Failed to submit turn data')
    }
    return resp.value
}

export const submitTurnDataStreaming = async (
    payload: SubmitTurnDataPayload
): Promise<ReadableStreamDefaultReader<Uint8Array> | undefined> => {
    const reader = await submitTurnDataStreamingApi(payload)
    if (!reader.isOk()) {
        throw new Error('Failed to fetch streaming response')
    }
    return reader.value
}

export const fillDraftData = async (
    userId: string,
    submissionId: string,
    mainInfo: ConversationMainInfoType | Conversationsv2MainInfoType,
    type: ConversationalTurnTypes.DraftFill | Conversationalv2TurnTypes.DraftFill
) => {
    const resp = await fillDraftDataApi(userId, submissionId, mainInfo, type)
    if (!resp.isOk()) {
        throw new Error(sanitizeAxiosError(resp.error) ?? 'Failed to fill draft data')
    }
    return resp.value
}

export const getJobHierarchy = async (jobId: string) => {
    const resp = await getJobHierarchyApi(jobId)
    if (!resp.isOk()) {
        throw new Error(sanitizeAxiosError(resp.error) ?? 'Failed to get job hierarchy')
    }
    return resp.value
}

export const getWorkflowStepsByJobId = async (jobId: string) => {
    const steps = await getWorkflowStepsByJobIdApi(jobId)
    if (!steps.isOk()) {
        throw new Error(sanitizeAxiosError(steps.error) ?? 'Failed to get job steps')
    }
    return steps.value
}

export const testToolCall = async (
    jobId: string,
    inputParams: JSONObject
): Promise<ToolCallResponseType> => {
    const resp = await testToolCallApi(jobId, inputParams)
    if (!resp.isOk()) {
        throw new Error(sanitizeAxiosError(resp.error) ?? 'Failed to test tool call')
    }
    return resp.value
}
